import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const transparentImage =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=";

function HomeHeroSection({ ImageUrls, VideoUrls, LinkUrls }) {
  const sectionRef = useRef(null);
  const videoRef = useRef(null); // Ref for the iframe
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const starRef = useRef(null);
  const buttonRef = useRef(null); // Ref for the button

  let imageItemUrls = ImageUrls;
  let videoItemUrls = VideoUrls;
  let linkItemUrls = LinkUrls;

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const star = starRef.current;
    const button = buttonRef.current;

    // Parallax effect for the background video (iframe)
    gsap.to(video, {
      y: '50%', // Moves the video up by 50% of its height as you scroll down
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    // Title animation (fade in and slight move up)
    gsap.fromTo(title,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Subtitle animation (fade in and slight move up, delayed)
    gsap.fromTo(subtitle,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        delay: 0.3,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: subtitle,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Star icon animation (pop in with a slight bounce)
    gsap.fromTo(star,
      { opacity: 0, scale: 0 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'back.out(1.7)',
        delay: 0.8, // Delay after title and subtitle
        scrollTrigger: {
          trigger: star,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Button animation (fade in and slight move up, delayed)
    gsap.fromTo(button,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        delay: 1.0, // Delay after star
        ease: 'power3.out',
        scrollTrigger: {
          trigger: button,
          start: 'top 90%', // Adjust start for button visibility
          toggleActions: 'play none none none',
        },
      }
    );

  }, []);

  return (
    <>
      <style>
        {`
          .HomeHero-section {
            position: relative;
            width: 100%;
            height: 80vh; /* Full viewport height */
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: white;
            // background-color:#808080;
            // opacity:0.9;
            text-align: center;
          }

          .HomeHero-background-iframe {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100vw; /* Full viewport width */
            height: 100vh; /* Full viewport height */
            transform: translate(-50%, -50%);
            pointer-events: none; /* Allows clicks to pass through to elements behind */
            z-index: -1;
            filter: brightness(0.1); /* Darken the video to make text stand out */
          }

          /* Aspect ratio correction for iframe to cover the screen */
          .HomeHero-background-iframe iframe {
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
          }

          /* This ensures the iframe covers the entire screen without black bars */
          @media (min-aspect-ratio: 16/9) {
            .HomeHero-background-iframe iframe {
              height: 56.25vw; /* 9 / 16 * 100 */
              min-height: 100%;
              width: 100%;
            }
          }

          @media (max-aspect-ratio: 16/9) {
            .HomeHero-background-iframe iframe {
              width: 177.78vh; /* 16 / 9 * 100 */
              min-width: 100%;
              height: 100%;
            }
          }

          /* Style for the transparent image behind the iframe */
          .HomeHero-background-iframe .transparent-alt-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0; /* Ensure it's behind the iframe but still within the container */
            pointer-events: none;
          }


          .HomeHero-content-overlay {
            position: relative;
            z-index: 1;
            padding: 20px;
          }

          .HomeHero-title {
            font-family: 'Impact', 'Arial Black', sans-serif; /* Closest common font */
            font-size: 7vw; /* Responsive font size */
            font-weight: 900;
            line-height: 0.9; /* Adjust line height for stacked text */
            margin: 0;
            text-transform: uppercase;
          }

          .HomeHero-subtitle {
            font-family: 'Impact', 'Arial Black', sans-serif;
            font-size: 7vw;
            font-weight: 900;
            line-height: 0.9;
            margin-top: 10px;
            text-transform: uppercase;
            display: flex;
            align-items: flex-end; /* Changed to flex-end to align items to the bottom */
            justify-content: center;
            margin-bottom: 0;
          }

          .HomeHero-star-icon {
            margin-left: 5px;
            font-size: 2vw;
            margin-bottom : 10px;
          }

          .HomeHero-enquire-button {
            background-color: white;
            color: black;
            border: none;
            padding: 15px 40px;
            margin-top: 40px;
            font-family: 'Arial', sans-serif; /* A more standard font for the button */
            font-size: 1.2vw; /* Responsive button font size */
            font-weight: 900;
            text-transform: uppercase;
            cursor: pointer;
            transition: background-color 0.3s ease;
            text-decoration: none; /* For anchor tag if used */
          }

          .HomeHero-enquire-button:hover {
            background-color: #f0f0f0;
          }

          /* Responsive adjustments - These will be overridden by HomeHeroSecMobile on smaller screens */
          @media (max-width: 768px) {
            .HomeHero-section {
              display: none; /* Hide this component on smaller screens */
            }
          }
        `}
      </style>
      <div className="HomeHero-section" ref={sectionRef}>
        <div>
          <img
            src={transparentImage}
            alt={linkItemUrls.link1.altText}
            className="transparent-alt-image"
          />
        </div>
        <div className="HomeHero-background-iframe">
          {/* Transparent image for alt text */}
          <img
            src={transparentImage}
            alt={videoItemUrls.video1.altText}
            className="transparent-alt-image"
          />
          <iframe
            src={videoItemUrls.video1.url}
            title="Background Video" // This title is for the iframe itself, not for screen readers in the same way alt text is.
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            ref={videoRef} // Add ref to the iframe
          ></iframe>
        </div>
        <div className="HomeHero-content-overlay">
          <h1 className="HomeHero-title" ref={titleRef}>ABCD</h1>
          <h2 className="HomeHero-subtitle" ref={subtitleRef}>
            STUDIOS
            <span className="HomeHero-star-icon" ref={starRef}>&#9733;</span> {/* Unicode star character */}
          </h2>
          <button className="HomeHero-enquire-button" ref={buttonRef}>ENQUIRE NOW</button>
        </div>
      </div>
    </>
  );
}

function HomeHeroSecMobile({ ImageUrls, VideoUrls }) {
  const sectionRef = useRef(null);
  const videoRef = useRef(null); // Ref for the iframe
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const starRef = useRef(null);
  const buttonRef = useRef(null); // Ref for the button

  let imageItemUrls = ImageUrls;
  let videoItemUrls = VideoUrls;

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const star = starRef.current;
    const button = buttonRef.current;

    // Parallax effect for the background video (iframe)
    gsap.to(video, {
      y: '50%', // Moves the video up by 50% of its height as you scroll down
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    // Title animation (fade in and slight move up)
    gsap.fromTo(title,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Subtitle animation (fade in and slight move up, delayed)
    gsap.fromTo(subtitle,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        delay: 0.3,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: subtitle,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Star icon animation (pop in with a slight bounce)
    gsap.fromTo(star,
      { opacity: 0, scale: 0 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'back.out(1.7)',
        delay: 0.8, // Delay after title and subtitle
        scrollTrigger: {
          trigger: star,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Button animation (fade in and slight move up, delayed)
    gsap.fromTo(button,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        delay: 1.0, // Delay after star
        ease: 'power3.out',
        scrollTrigger: {
          trigger: button,
          start: 'top 90%', // Adjust start for button visibility
          toggleActions: 'play none none none',
        },
      }
    );

  }, []);

  return (
    <>
      <style>
        {`
          .HomeHeroSecMobile-section {
            position: relative;
            width: 100%;
            height: 90vh; /* Full viewport height */
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: white;
            text-align: center;

          }

          .HomeHeroSecMobile-background-iframe {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100vw; /* Full viewport width */
            height: 100vh; /* Full viewport height */
            transform: translate(-50%, -50%);
            pointer-events: none; /* Allows clicks to pass through to elements behind */
            z-index: -1;
            filter: brightness(0.1); /* Darken the video to make text stand out */
          }

          /* Aspect ratio correction for iframe to cover the screen */
          @media (min-aspect-ratio: 16/9) {
            .HomeHeroSecMobile-background-iframe iframe {
              height: 56.25vw; /* 9 / 16 * 100 */
              min-height: 100%;
              width: 100%;
            }
          }

          @media (max-aspect-ratio: 16/9) {
            .HomeHeroSecMobile-background-iframe iframe {
              width: 177.78vh; /* 16 / 9 * 100 */
              min-width: 100%;
              height: 100%;
            }
          }

          /* Style for the transparent image behind the iframe */
          .HomeHeroSecMobile-background-iframe .transparent-alt-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0; /* Ensure it's behind the iframe but still within the container */
            pointer-events: none;
          }


          .HomeHeroSecMobile-content-overlay {
            position: relative;
            z-index: 1;
            padding: 20px;
          }

          .HomeHeroSecMobile-title {
            font-family: 'Impact', 'Arial Black', sans-serif; /* Closest common font */
            font-size: 20vw; /* Responsive font size for mobile */
            font-weight: 900;
            line-height: 0.9; /* Adjust line height for stacked text */
            margin: 0;
            text-transform: uppercase;
          }

          .HomeHeroSecMobile-subtitle {
            font-family: 'Impact', 'Arial Black', sans-serif;
            font-size: 20vw; /* Responsive font size for mobile */
            font-weight: 900;
            line-height: 0.9;
            margin-top: 10px;
            text-transform: uppercase;
            display: flex;
            align-items: flex-end; /* Changed to flex-end to align items to the bottom */
            justify-content: center;
            margin-bottom: 0;
          }

          .HomeHeroSecMobile-star-icon {
            margin-left: 10px; /* Adjusted for smaller screens */
            font-size: 7vw; /* Adjusted for smaller screens */
            margin-bottom : 10px;
          }

          .HomeHeroSecMobile-enquire-button {
            background-color: white;
            color: black;
            border: none;
            padding: 12px 25px; /* Adjusted for smaller screens */
            margin-top: 40px;
            font-family: 'Arial', sans-serif; /* A more standard font for the button */
            font-size: 3vw; /* Responsive button font size for mobile */
            font-weight: 900;
            text-transform: uppercase;
            cursor: pointer;
            transition: background-color 0.3s ease;
            text-decoration: none; /* For anchor tag if used */
          }

          .HomeHeroSecMobile-enquire-button:hover {
            background-color: #f0f0f0;
          }

          /* Show this component only on smaller screens */
          @media (min-width: 769px) {
            .HomeHeroSecMobile-section {
              display: none;
            }
          }
        `}
      </style>
      <div className="HomeHeroSecMobile-section" ref={sectionRef}>
        <div className="HomeHeroSecMobile-background-iframe">
          {/* Transparent image for alt text */}
          <img
            src={transparentImage}
            alt={videoItemUrls.video1.altText}
            className="transparent-alt-image"
          />
          <iframe
            src={videoItemUrls.video1.url}
            title="Background Video"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            ref={videoRef} // Add ref to the iframe
          ></iframe>
        </div>
        <div className="HomeHeroSecMobile-content-overlay">
          <h1 className="HomeHeroSecMobile-title" ref={titleRef}>ABCD</h1>
          <h2 className="HomeHeroSecMobile-subtitle" ref={subtitleRef}>
            STUDIOS
            <span className="HomeHeroSecMobile-star-icon" ref={starRef}>&#9733;</span> {/* Unicode star character */}
          </h2>
          <button className="HomeHeroSecMobile-enquire-button" ref={buttonRef}>ENQUIRE NOW</button>
        </div>
      </div>
    </>
  );
}

export { HomeHeroSection, HomeHeroSecMobile };